import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useSession, whereWeWere } from './auth/session.tsx';
import { SignIn } from './pages/SignIn.tsx';
import { ClientRecord } from './pages/ClientRecord.tsx';
import { RecordVisit } from './pages/RecordVisit.tsx';
import { ClientList } from './pages/ClientList.tsx';
import { HeldClients } from './pages/HeldClients.tsx';
import { DischargeConfirmation } from './pages/DischargeConfirmation.tsx';
import { MyTasks } from './pages/MyTasks.tsx';
import { DepartmentQueue } from './pages/DepartmentQueue.tsx';
import { Triage } from './pages/Triage.tsx';
import { MyTime } from './pages/MyTime.tsx';
import { DepartmentTime } from './pages/DepartmentTime.tsx';
import { ReleaseHeld } from './pages/ReleaseHeld.tsx';
import { OverdueByDepartment } from './pages/OverdueByDepartment.tsx';
import { DirectorDashboard } from './pages/DirectorDashboard.tsx';
import { ComplianceDashboard } from './pages/ComplianceDashboard.tsx';
import { CommissionTracker } from './pages/CommissionTracker.tsx';
import { FinanceDashboard } from './pages/FinanceDashboard.tsx';
import { ExecutiveDashboard } from './pages/ExecutiveDashboard.tsx';
import { AppShell } from './components/AppShell.tsx';
import { CompanyPolicies } from './pages/CompanyPolicies.tsx';
import { GeocodeBacklog } from './pages/GeocodeBacklog.tsx';
import { AddClient } from './pages/AddClient.tsx';
import { RealEstate } from './pages/RealEstate.tsx';
import { UntypedBuildings } from './pages/UntypedBuildings.tsx';
import { Facilities } from './pages/Facilities.tsx';
import { FacilityPage } from './pages/FacilityPage.tsx';
import { KindReview } from './pages/KindReview.tsx';
import { FoundInDocuments } from './pages/FoundInDocuments.tsx';
import { BreakGlassUse } from './pages/BreakGlassUse.tsx';
import { Home } from './pages/Home.tsx';
import { UnmatchedCommunications } from './pages/UnmatchedCommunications.tsx';
import { Lookup } from './pages/Lookup.tsx';
import { ClioCounts } from './pages/ClioCounts.tsx';
import { ClioCopy } from './pages/ClioCopy.tsx';
import { Reading } from './pages/Reading.tsx';
import { ReadingReview } from './pages/ReadingReview.tsx';
import { ContactDuplicates } from './pages/ContactDuplicates.tsx';
import { VisitsDue } from './pages/VisitsDue.tsx';
import { VisitItinerary } from './pages/VisitItinerary.tsx';
import { ReservedModule } from './pages/ReservedModule.tsx';
import { useToolbar } from './api/navigation.ts';

/**
 * Where each role lands is the server's answer (GET /me/toolbar, `landing`,
 * from api/src/routes/navigation.ts): the brief's landings by primary role,
 * the client list for anyone not named, and the lookup for a reader with no
 * client pages.
 */
function Landing() {
  const toolbar = useToolbar();
  // isPending, not isLoading: between retries isLoading drops to false while
  // there is still no answer, which would land the user on the fallback.
  if (toolbar.isPending) return <p className="p-6 text-neutral-600">Loading…</p>;
  return <Navigate to={toolbar.data?.landing ?? '/clients'} replace />;
}

/**
 * An old address, kept working (the navigation rebuild, 14 October). Every URL
 * the app used before keeps resolving, with its filters, so bookmarks and links
 * in emails still arrive where the page now lives.
 */
function Moved({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function MovedVisit() {
  const { id = '' } = useParams();
  return <Moved to={`/clients/${id}/social-work/visit`} />;
}

export function App() {
  const { isSignedIn } = useSession();
  const navigate = useNavigate();

  // After a sign-in redirect, put the user back where they were.
  useEffect(() => {
    if (!isSignedIn) return;
    const to = whereWeWere();
    if (to && to !== location.pathname + location.search) navigate(to, { replace: true });
  }, [isSignedIn, navigate]);

  if (!isSignedIn) return <SignIn />;

  return (
    <AppShell>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/home" element={<Home />} />
      <Route path="/lookup" element={<Lookup />} />
      <Route path="/communications/unmatched" element={<UnmatchedCommunications />} />
      <Route path="/admin/clio-counts" element={<ClioCounts />} />
      <Route path="/admin/clio-copy" element={<ClioCopy />} />
      <Route path="/admin/reading" element={<Reading />} />
      <Route path="/admin/reading/docs/:id" element={<ReadingReview />} />
      <Route path="/contacts/duplicates" element={<ContactDuplicates />} />

      {/* Clients */}
      <Route path="/clients" element={<ClientList />} />
      <Route path="/clients/new" element={<AddClient />} />
      <Route path="/clients/held" element={<HeldClients />} />
      <Route path="/clients/discharge-confirmation" element={<DischargeConfirmation />} />
      <Route path="/clients/:id" element={<ClientRecord />} />
      <Route path="/clients/:id/social-work/visit" element={<RecordVisit />} />
      <Route path="/clients/:id/sw/visit" element={<MovedVisit />} />
      <Route path="/clients/:id/visit" element={<MovedVisit />} />
      <Route path="/clients/:id/:section" element={<ClientRecord />} />
      {/* A-#195 1: a property is its own page, inside the client record */}
      <Route path="/clients/:id/property/:propertyId" element={<ClientRecord />} />

      {/* Visits */}
      <Route path="/visits" element={<VisitsDue />} />
      <Route path="/visits/board" element={<DirectorDashboard />} />
      <Route path="/visits/itinerary" element={<VisitItinerary />} />
      <Route path="/facilities" element={<Facilities />} />
      <Route path="/facilities/:id" element={<FacilityPage />} />

      {/* Legal, Finance, Compensation, Reports, Tasks, Documents, Real Estate */}
      <Route path="/legal" element={<CommissionTracker />} />
      <Route path="/legal/appointments" element={<KindReview />} />
      <Route path="/legal/found-in-documents" element={<FoundInDocuments />} />
      <Route path="/finance" element={<ReservedModule which="finance" />} />
      <Route path="/financials" element={<Moved to="/finance" />} />
      <Route path="/compensation" element={<FinanceDashboard />} />
      <Route path="/reports" element={<ComplianceDashboard />} />
      <Route path="/tasks" element={<MyTasks />} />
      <Route path="/tasks/queue" element={<DepartmentQueue />} />
      <Route path="/tasks/triage" element={<Triage />} />
      <Route path="/time/mine" element={<MyTime />} />
      <Route path="/time/department" element={<DepartmentTime />} />
      <Route path="/documents" element={<ReservedModule which="documents" />} />
      <Route path="/real-estate" element={<RealEstate />} />

      {/* The top-right menu */}
      <Route path="/agency" element={<ExecutiveDashboard />} />
      <Route path="/facilities/needing-type" element={<UntypedBuildings />} />
      <Route path="/geocoding" element={<GeocodeBacklog />} />
      <Route path="/break-glass" element={<BreakGlassUse />} />
      <Route path="/policies" element={<CompanyPolicies />} />
      <Route path="/release-held" element={<ReleaseHeld />} />
      <Route path="/overdue" element={<OverdueByDepartment />} />

      {/* Old addresses: every one redirects to its new home, filters and all. */}
      <Route path="/work" element={<Moved to="/tasks" />} />
      <Route path="/dashboards" element={<Moved to="/" />} />
      <Route path="/dashboards/executive" element={<Moved to="/agency" />} />
      <Route path="/dashboards/director" element={<Moved to="/visits/board" />} />
      <Route path="/dashboards/compliance" element={<Moved to="/reports" />} />
      <Route path="/dashboards/commission" element={<Moved to="/legal" />} />
      <Route path="/dashboards/finance" element={<Moved to="/compensation" />} />
      <Route path="/appointments/kind-review" element={<Moved to="/legal/appointments" />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AppShell>
  );
}
